import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExternalLink, Gift } from 'react-feather';
import Button from 'app/seedly-component-library/button-new';
import { useIsEnrolledUser, useIsSignedIn } from 'entity/user/userHooks';
import { markRewardUseRequest } from 'entity/reward/rewardRedux';

import { Reward, RewardClaim, TierMap } from 'entity/reward/rewardTypes';
import ConfirmationModal from 'components/post/ConfirmationModal';
import Link from 'components/misc/Link';
import { RootState } from 'reducer';
import isEmpty from 'lodash/isEmpty';
import Image from 'next/image';
import { rewardOnboarding } from 'theme';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/nextjs';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import RewardSignUpCard from '../../RewardSignUpCard';
import useClaimReward from './useClaimReward';

interface OwnProps {
  reward: Reward;
  claim: RewardClaim;
  setOpenConfirmationModal?: (params: boolean) => void;
}

const checkIsRedeemable = (
  profile,
  reward: Reward,
): {
  isRedeemable: boolean;
  reason: React.ReactNode;
} => {
  if (isEmpty(profile) || !reward) {
    return {
      isRedeemable: false,
      reason: null,
    };
  }

  // not enrolled
  if (!profile?.isTwoFactorAuthCompleted) {
    return {
      isRedeemable: false,
      reason: null,
    };
  }

  // not enough tier
  if (profile.tier < reward.minTier) {
    return {
      isRedeemable: false,
      reason: (
        <span
          className="text-xs lg:text-sm text-center"
          onClick={() => {
            mixpanel.track(trackedEvents.RewardsCollection, {
              param: 'find_out_more_how_to_earn',
            });
          }}
        >
          You&apos;re almost there! Unlock new tiers to redeem this reward.
          <Link href="/rewards/about">
            <a className="text-xs lg:text-sm text-blue-500">Learn more.</a>
          </Link>
        </span>
      ),
    };
  }

  // not enough credit
  if (profile.creditsCount < reward.minCredits) {
    return {
      isRedeemable: false,
      reason: (
        <p className="text-xs lg:text-sm text-center">
          You need{' '}
          {(reward.minCredits - profile.creditsCount)?.toLocaleString()} more
          credits! Find out how to earn more credits{' '}
          <Link href="/rewards/about">
            <a className="text-xs lg:text-sm text-blue-500">here</a>
          </Link>
          .
        </p>
      ),
    };
  }

  // fully redeemed
  if (reward.isFullyRedeemed) {
    return {
      isRedeemable: false,
      reason: (
        <p className="text-xs lg:text-sm text-center">
          This reward has been fully redeemed.
        </p>
      ),
    };
  }

  return {
    isRedeemable: true,
    reason: '',
  };
};

const useBottomSectionContent = (props: OwnProps) => {
  const { reward, setOpenConfirmationModal, claim } = props;

  const isSignedIn = useIsSignedIn();
  const isEnrolled = useIsEnrolledUser();
  const profile = useSelector((state: RootState) => state.profile.user);
  const redeemable = checkIsRedeemable(profile, reward);
  const handleClaimReward = useClaimReward(reward, claim);
  const dispatch = useDispatch();
  const isClaiming = useSelector(
    (state: RootState) => state.reward.api.isClaiming,
  );

  if (!reward) return null;

  if (!isSignedIn) {
    return <RewardSignUpCard />;
  }

  // reward claimed but not used yet
  if (claim && !claim?.used_at) {
    return (
      <Button
        onClick={() => {
          handleClaimReward();
        }}
        isBlock
      >
        {(claim?.reward_type === 'url' || claim?.reward_type === 'text') && (
          <>
            Use Now
            <ExternalLink className="pl-1" size={18} color="white" />
          </>
        )}
        {claim?.reward_type === 'file' && <>Download Now</>}
        {!claim?.reward_type && <>Use Now</>}
      </Button>
    );
  }

  // reward claimed and used, to be mark as unused here
  if (claim && claim?.used_at) {
    const usedDate = dayjs(claim.used_at).format('DD MMM YYYY');
    return (
      <div className="flex flex-col w-full items-center gap-3">
        <p className="text-xs text-neutral-500">
          This reward was used on {usedDate}.
        </p>
        <Button
          variant="outlineGrey"
          onClick={() => {
            ConfirmationModal({
              title: 'Mark as unused?',
              content: 'Only mark a reward as unused, if it hasn’t been used.',
              okText: 'Confirm',
              cancelText: 'Cancel',
            })
              .then(() =>
                dispatch(
                  markRewardUseRequest({
                    rewardId: reward.id,
                    claimId: claim?.id,
                  }),
                ),
              )
              .catch(e => Sentry.captureException(e));
          }}
          isBlock
        >
          Mark as unused
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      {isEnrolled ? (
        <div className="flex items-center">
          <p className="text-xs font-bold text-neutral-500">YOU HAVE:</p>
          <div className="pt-1 pl-1 pr-[2px]">
            <Image src={rewardOnboarding.singleCoin} width={20} height={20} />
          </div>
          <span className="text-base font-bold">
            {profile.creditsCount?.toLocaleString()} Credits (
            {TierMap[profile.tier]})
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <Gift />
          <p className="text-sm font-bold">
            Join SeedlyRewards to redeem this reward!
          </p>
        </div>
      )}
      <Button
        disabled={!redeemable.isRedeemable}
        isBlock
        size="lg"
        onClick={() => {
          if (isEnrolled) {
            setOpenConfirmationModal(true);
          }
        }}
        isLoading={isClaiming}
      >
        {isEnrolled
          ? `Redeem with ${reward.minCredits?.toLocaleString()} Credits`
          : 'Join now'}
      </Button>
      {redeemable.reason}
    </div>
  );
};

export default useBottomSectionContent;
